import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';

export type SearchResultType = 'post' | 'user' | 'prayer' | 'bible_study';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  description?: string;
  category?: string;
  avatar_url?: string;
  created_at?: string;
  metadata?: {
    likes?: number;
    comments?: number;
    participants?: number;
    schedule?: string;
  };
}

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isLoading: boolean;
  error: string | null;
  search: (searchQuery: string) => Promise<void>;
  clearResults: () => void;
}

export function useGlobalSearch(): UseGlobalSearchReturn {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchTerm = `%${searchQuery}%`;
      const allResults: SearchResult[] = [];

      const [postsResult, usersResult, prayersResult, studiesResult] = await Promise.all([
        supabase
          .from('posts')
          .select(`
            id,
            title,
            content,
            category,
            likes,
            comments,
            created_at,
            profiles!fk_posts_author(first_name, last_name, avatar_url)
          `)
          .eq('moderation_status', 'approved')
          .or(`title.ilike.${searchTerm},content.ilike.${searchTerm}`)
          .order('created_at', { ascending: false })
          .limit(5),

        supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url, bio, church, created_at')
          .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm}`)
          .limit(5),

        supabase
          .from('prayer_requests')
          .select(`
            id,
            title,
            content,
            category,
            is_anonymous,
            prayer_count,
            created_at,
            profiles!fk_prayer_requests_author(first_name, last_name, avatar_url)
          `)
          .or(`title.ilike.${searchTerm},content.ilike.${searchTerm}`)
          .order('created_at', { ascending: false })
          .limit(5),

        supabase
          .from('bible_studies')
          .select(`
            id,
            title,
            description,
            schedule,
            difficulty,
            status,
            max_participants,
            created_at,
            profiles!fk_bible_studies_leader(first_name, last_name, avatar_url)
          `)
          .or(`title.ilike.${searchTerm},description.ilike.${searchTerm}`)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(5),
      ]);

      if (postsResult.data) {
        postsResult.data.forEach((post) => {
          const profile = Array.isArray(post.profiles) ? post.profiles[0] : post.profiles;
          const authorName = profile 
            ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() 
            : 'Unknown';
          
          allResults.push({
            id: post.id,
            type: 'post',
            title: post.title,
            subtitle: `by ${authorName}`,
            description: post.content?.substring(0, 100) + (post.content && post.content.length > 100 ? '...' : ''),
            category: post.category,
            avatar_url: profile?.avatar_url,
            created_at: post.created_at,
            metadata: {
              likes: post.likes || 0,
              comments: post.comments || 0,
            },
          });
        });
      }

      if (usersResult.data) {
        usersResult.data.forEach((user) => {
          const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
          
          allResults.push({
            id: user.id,
            type: 'user',
            title: fullName,
            subtitle: user.church || undefined,
            description: user.bio?.substring(0, 100) + (user.bio && user.bio.length > 100 ? '...' : ''),
            avatar_url: user.avatar_url,
            created_at: user.created_at,
          });
        });
      }

      if (prayersResult.data) {
        prayersResult.data.forEach((prayer) => {
          const profile = Array.isArray(prayer.profiles) ? prayer.profiles[0] : prayer.profiles;
          const authorName = prayer.is_anonymous 
            ? 'Anonymous' 
            : (profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Unknown');
          
          allResults.push({
            id: prayer.id,
            type: 'prayer',
            title: prayer.title,
            subtitle: `by ${authorName}`,
            description: prayer.content?.substring(0, 100) + (prayer.content && prayer.content.length > 100 ? '...' : ''),
            category: prayer.category || undefined,
            avatar_url: prayer.is_anonymous ? undefined : profile?.avatar_url,
            created_at: prayer.created_at,
            metadata: {
              likes: prayer.prayer_count || 0,
            },
          });
        });
      }

      if (studiesResult.data) {
        studiesResult.data.forEach((study) => {
          const profile = Array.isArray(study.profiles) ? study.profiles[0] : study.profiles;
          const leaderName = profile 
            ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() 
            : 'Unknown';
          
          allResults.push({
            id: study.id,
            type: 'bible_study',
            title: study.title,
            subtitle: `Led by ${leaderName}`,
            description: study.description?.substring(0, 100) + (study.description && study.description.length > 100 ? '...' : ''),
            category: study.difficulty || undefined,
            avatar_url: profile?.avatar_url,
            created_at: study.created_at,
            metadata: {
              schedule: study.schedule || undefined,
              participants: study.max_participants || undefined,
            },
          });
        });
      }

      setResults(allResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    search,
    clearResults,
  };
}
